import { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';

interface CompositeCaptureProps {
  onCapture?: (imageBlob: Blob) => void;
  className?: string;
  avatarContainerRef: React.RefObject<HTMLDivElement>;
  onCaptureAvatar?: () => Promise<HTMLCanvasElement | null>;
}

interface CompositeCaptureRef {
  capturePhoto: () => Promise<void>;
  initializeCamera: () => Promise<void>;
}

export const CompositeCapture = forwardRef<CompositeCaptureRef, CompositeCaptureProps>(({ 
  onCapture, 
  className, 
  avatarContainerRef,
  onCaptureAvatar
}, ref) => {
  console.log('CompositeCapture component rendering');
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const compositeCanvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [error, setError] = useState<string>('');
  const [showFlash, setShowFlash] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);

  const initializeCameraInternal = useCallback(async (retryCount = 0) => {
    console.log(`initializeCamera called with retryCount: ${retryCount}`);
    const maxRetries = 2;
    
    try {
      // Check if camera permission is already granted
      const permission = await navigator.permissions.query({ name: 'camera' as PermissionName });
      
      if (permission.state === 'denied') {
        setHasPermission(false);
        setError('Camera permission denied. Please enable camera access in your browser settings.');
        return;
      }

      // Request camera access
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          facingMode: 'user'
        },
        audio: false
      });

      setStream(mediaStream);
      setHasPermission(true);
      setError('');

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        
        // Add event listeners to monitor video loading
        const video = videoRef.current;
        video.onloadedmetadata = () => {
          console.log('Video metadata loaded:', {
            width: video.videoWidth,
            height: video.videoHeight,
            readyState: video.readyState
          });
        };
        
        video.oncanplay = () => {
          console.log('Video can play:', {
            width: video.videoWidth,
            height: video.videoHeight,
            readyState: video.readyState
          });
        };
        
        video.onerror = (e) => {
          console.error('Video element error:', e);
          setError('Video playback error. Please refresh the page.');
        };
        
        video.play().catch(err => {
          console.error('Video play failed:', err);
          setError('Failed to start video playback. Please check your camera permissions.');
        });
      }

    } catch (err) {
      console.error(`Error accessing camera (attempt ${retryCount + 1}):`, err);
      
      // Retry for certain transient errors
      if (err instanceof Error && retryCount < maxRetries) {
        const retryableErrors = ['NotReadableError', 'AbortError'];
        if (retryableErrors.includes(err.name)) {
          console.log(`Retrying camera access in 1 second... (attempt ${retryCount + 2})`);
          setTimeout(() => {
            initializeCameraInternal(retryCount + 1);
          }, 1000);
          return;
        }
      }
      
      setHasPermission(false);
      
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          setError('Camera permission denied. Please allow camera access and refresh the page.');
        } else if (err.name === 'NotFoundError') {
          setError('No camera found. Please connect a camera and try again.');
        } else if (err.name === 'NotReadableError') {
          setError('Camera is currently in use by another application. Please close other camera apps and try again.');
        } else if (err.name === 'OverconstrainedError') {
          setError('Camera does not support the required settings. Try using a different camera.');
        } else if (err.name === 'AbortError') {
          setError('Camera access was interrupted. Please try again.');
        } else {
          setError(`Camera error: ${err.message || 'Please check your camera settings and try again.'}`);
        }
      } else {
        setError('Unknown camera error. Please refresh the page and try again.');
      }
    }
  }, []);

  useEffect(() => {
    console.log('CompositeCapture useEffect triggered');
    
    // Try to initialize immediately
    initializeCameraInternal();
    
    // Simple fallback - try once after 5 seconds if camera still not working
    const fallbackTimeout = setTimeout(() => {
      console.log('Fallback timeout triggered - checking camera state');
      if (!stream && hasPermission !== false && videoRef.current && !videoRef.current.srcObject) {
        console.log('Camera not initialized, trying one fallback initialization');
        initializeCameraInternal(0); // Start fresh with retryCount 0
      }
    }, 5000);

    // Cleanup function
    return () => {
      console.log('CompositeCapture useEffect cleanup called');
      clearTimeout(fallbackTimeout);
      if (stream) {
        console.log('Stopping camera stream tracks');
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const captureAvatarScene = (): Promise<HTMLCanvasElement | null> => {
    if (onCaptureAvatar) {
      return onCaptureAvatar();
    }
    return Promise.resolve(null);
  };

  const captureCompositePhoto = useCallback(async () => {
    if (isCapturing) {
      console.log('Capture already in progress, skipping...');
      return;
    }

    console.log('Capture attempt:', {
      video: !!videoRef.current,
      canvas: !!canvasRef.current,
      compositeCanvas: !!compositeCanvasRef.current,
      stream: !!stream,
      videoReady: videoRef.current?.readyState,
    });

    // Check if video element has an active stream (more reliable than state)
    const videoElement = videoRef.current;
    const hasActiveStream = videoElement?.srcObject && (videoElement.srcObject as MediaStream).active;
    
    if (!videoElement || !canvasRef.current || !compositeCanvasRef.current || (!stream && !hasActiveStream)) {
      console.error('Video or canvas not ready:', {
        video: !!videoElement,
        canvas: !!canvasRef.current,
        compositeCanvas: !!compositeCanvasRef.current,
        stream: !!stream,
        streamActive: stream?.active,
        streamTracks: stream?.getTracks().length,
        hasActiveStream: !!hasActiveStream,
        videoSrcObject: !!videoElement?.srcObject,
        videoSrcObjectActive: videoElement?.srcObject ? (videoElement.srcObject as MediaStream).active : false
      });
      setIsCapturing(false);
      return;
    }

    // Wait for video to be ready if it's not
    if (videoElement.readyState < 2) { // Less than HAVE_CURRENT_DATA
      console.log('Video not ready, waiting...', {
        readyState: videoElement.readyState,
        currentTime: videoElement.currentTime,
        paused: videoElement.paused
      });
      
      // Try to wait for the video to load
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Video load timeout'));
        }, 3000);

        const checkReady = () => {
          if (videoElement.readyState >= 2) {
            clearTimeout(timeout);
            resolve();
          } else {
            setTimeout(checkReady, 100);
          }
        };
        
        checkReady();
      }).catch(err => {
        console.error('Video failed to become ready:', err);
        setIsCapturing(false);
        return;
      });
    }

    // Check if video has valid dimensions
    if (videoElement.videoWidth === 0 || videoElement.videoHeight === 0) {
      console.error('Video has invalid dimensions:', {
        width: videoElement.videoWidth,
        height: videoElement.videoHeight,
        readyState: videoElement.readyState,
        currentTime: videoElement.currentTime,
        paused: videoElement.paused,
        ended: videoElement.ended,
        networkState: videoElement.networkState,
        error: videoElement.error
      });
      
      // Try to recover by reloading the video
      if (videoElement.srcObject) {
        console.log('Attempting to recover video...');
        videoElement.load();
        setTimeout(() => {
          videoElement.play().catch(err => {
            console.error('Recovery play failed:', err);
          });
        }, 100);
      }
      
      setIsCapturing(false);
      return;
    }

    setIsCapturing(true);

    // Show flash effect
    setShowFlash(true);
    setTimeout(() => setShowFlash(false), 200);

    const video = videoRef.current;
    const webcamCanvas = canvasRef.current;
    const compositeCanvas = compositeCanvasRef.current;
    const webcamCtx = webcamCanvas.getContext('2d');
    const compositeCtx = compositeCanvas.getContext('2d');

    if (!webcamCtx || !compositeCtx) {
      console.error('Cannot get canvas context');
      return;
    }

    // Set canvas sizes
    webcamCanvas.width = video.videoWidth;
    webcamCanvas.height = video.videoHeight;

    // Capture webcam frame (flip horizontally to match mirror effect)
    webcamCtx.scale(-1, 1);
    webcamCtx.drawImage(video, -webcamCanvas.width, 0, webcamCanvas.width, webcamCanvas.height);

    // Capture avatar scene
    const avatarCanvas = await captureAvatarScene();

    // Calculate square dimensions - use the smaller dimension for final square
    const webcamSquareSize = Math.min(webcamCanvas.width, webcamCanvas.height);
    const finalSquareSize = webcamSquareSize;

    // Set composite canvas size to square format (1:1)
    compositeCanvas.width = finalSquareSize;
    compositeCanvas.height = finalSquareSize;

    // Crop webcam to center square
    const webcamCropX = (webcamCanvas.width - webcamSquareSize) / 2;
    const webcamCropY = (webcamCanvas.height - webcamSquareSize) / 2;

    // Draw webcam feed as background (cropped to center square)
    compositeCtx.drawImage(
      webcamCanvas,
      webcamCropX, webcamCropY, webcamSquareSize, webcamSquareSize, // Source crop
      0, 0, finalSquareSize, finalSquareSize // Destination (fill entire square)
    );

    // Overlay avatar on top if available
    if (avatarCanvas) {
      // Calculate crop for avatar (center square)
      const avatarSquareSize = Math.min(avatarCanvas.width, avatarCanvas.height);
      const avatarCropX = (avatarCanvas.width - avatarSquareSize) / 2;
      const avatarCropY = (avatarCanvas.height - avatarSquareSize) / 2;

      // Draw avatar overlaid on top (full opacity)
      compositeCtx.drawImage(
        avatarCanvas,
        avatarCropX, avatarCropY, avatarSquareSize, avatarSquareSize, // Source crop
        0, 0, finalSquareSize, finalSquareSize // Destination (overlay on entire square)
      );
    }

    // Add photo frame overlay on top of everything
    try {
      const overlayImage = new Image();
      await new Promise<void>((resolve, reject) => {
        overlayImage.onload = () => resolve();
        overlayImage.onerror = reject;
        overlayImage.src = '/img/PhotoboothOverlay.png';
      });

      // Draw overlay frame at full size (it's already 1:1 aspect ratio)
      compositeCtx.drawImage(
        overlayImage,
        0, 0, finalSquareSize, finalSquareSize // Draw overlay to fill entire square
      );
    } catch (error) {
      console.warn('Could not load photo overlay:', error);
    }

    // Convert composite canvas to blob
    compositeCanvas.toBlob((blob) => {
      if (blob && onCapture) {
        onCapture(blob);
      }
    }, 'image/jpeg', 0.9);

    setIsCapturing(false);
  }, [stream, onCapture, captureAvatarScene, isCapturing]);

  useImperativeHandle(ref, () => ({
    capturePhoto: captureCompositePhoto,
    initializeCamera: initializeCameraInternal
  }), [captureCompositePhoto, initializeCameraInternal]);

  const requestPermission = async (retryCount = 0) => {
    const maxRetries = 2;
    
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          facingMode: 'user'
        },
        audio: false
      });

      setStream(mediaStream);
      setHasPermission(true);
      setError('');

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.play();
      }

    } catch (err) {
      console.error(`Error requesting camera permission (attempt ${retryCount + 1}):`, err);
      
      // Retry for certain transient errors
      if (err instanceof Error && retryCount < maxRetries) {
        const retryableErrors = ['NotReadableError', 'AbortError'];
        if (retryableErrors.includes(err.name)) {
          console.log(`Retrying camera permission request in 1 second... (attempt ${retryCount + 2})`);
          setTimeout(() => {
            requestPermission(retryCount + 1);
          }, 1000);
          return;
        }
      }
      
      setHasPermission(false);
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          setError('Camera permission denied. Please allow camera access in your browser settings.');
        } else if (err.name === 'NotFoundError') {
          setError('No camera found. Please connect a camera and try again.');
        } else if (err.name === 'NotReadableError') {
          setError('Camera is currently in use by another application. Please close other camera apps and try again.');
        } else if (err.name === 'OverconstrainedError') {
          setError('Camera does not support the required settings. Try using a different camera.');
        } else if (err.name === 'AbortError') {
          setError('Camera access was interrupted. Please try again.');
        } else {
          setError(`Camera error: ${err.message || 'Please check your camera settings and try again.'}`);
        }
      } else {
        setError('Unknown camera error. Please refresh the page and try again.');
      }
    }
  };

  // Permission denied state
  if (hasPermission === false) {
    return (
      <div className={`flex flex-col items-center justify-center bg-gray-900 text-white p-8 ${className}`}>
        <svg className="w-16 h-16 mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />
        </svg>
        <h3 className="text-xl font-bold mb-2">Camera Access Required</h3>
        <p className="text-center text-sm opacity-80 mb-4 max-w-md">{error}</p>
        <button
          onClick={requestPermission}
          className="bg-purple-600 hover:bg-purple-700 px-6 py-2 rounded-lg transition-colors"
        >
          Enable Camera
        </button>
      </div>
    );
  }

  // Loading state
  if (hasPermission === null) {
    return (
      <div className={`flex items-center justify-center bg-gray-900 text-white ${className}`}>
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-4"></div>
          <p>Initializing camera...</p>
        </div>
      </div>
    );
  }

  // Camera ready state
  return (
    <div className={`relative ${className} overflow-hidden`}>
      <video
        ref={videoRef}
        className="w-full h-full object-cover transform scale-x-[-1]" // Mirror effect
        style={{
          objectPosition: 'center'
        }}
        playsInline
        muted
      />
      <canvas
        ref={canvasRef}
        className="hidden" // Hidden canvas for webcam capture
      />
      <canvas
        ref={compositeCanvasRef}
        className="hidden" // Hidden canvas for composite result
      />
      
      {/* Flash effect overlay */}
      {showFlash && (
        <div className="absolute inset-0 bg-white opacity-90 z-30 pointer-events-none" />
      )}
      
    </div>
  );
});
